import React, { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { formatCurrency, formatQuantity } from '../../utils/formatters'
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Alert,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Print as PrintIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  AttachMoney as MoneyIcon,
  Inventory as InventoryIcon,
  Receipt as ReceiptIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material'
import { format } from 'date-fns'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'
import { getCompanyUom } from '../../utils/companyUom'
import { LoadingSpinner, StatusChip } from '../../components/ui'
import { useLoadFormHistory, usePrintLoadForm, usePrintLoadFormWithInvoices, useDeleteLoadForm } from '../../hooks/api/useLoadForms'


const LoadFormDetailsPage: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth)
  const uom = getCompanyUom(user?.company_uom)
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [expandedBills, setExpandedBills] = useState<Set<string>>(new Set())
  const [activeTab, setActiveTab] = useState(0)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  // Use the actual API hook
  const { data: loadFormData, isLoading, error } = useLoadFormHistory(id!)
  const printLoadFormMutation = usePrintLoadForm()
  const printLoadFormWithInvoicesMutation = usePrintLoadFormWithInvoices()
  const deleteMutation = useDeleteLoadForm()

  // Debug: Log the data structure to understand the API response
  React.useEffect(() => {
    if (loadFormData) {
      console.log('Load Form Data Structure:', loadFormData)
      if (loadFormData.loadForm?.skus?.length > 0) {
        console.log('First SKU structure:', loadFormData.loadForm.skus[0])
      }
      if (loadFormData.loadForm?.bill_ids?.length > 0 && loadFormData.loadForm.bill_ids[0]?.sku_items?.length > 0) {
        console.log('First Bill SKU Item structure:', loadFormData.loadForm.bill_ids[0].sku_items[0])
      }
    }
  }, [loadFormData])

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handlePrint = () => {
    if (id) {
      printLoadFormMutation.mutate(id)
      handleMenuClose()
    }
  }

  const handlePrintWithInvoices = () => {
    if (id) {
      printLoadFormWithInvoicesMutation.mutate(id)
      handleMenuClose()
    }
  }

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true)
    handleMenuClose()
  }

  const handleDeleteConfirm = async () => {
    if (id) {
      try {
        await deleteMutation.mutateAsync(id)
        setDeleteDialogOpen(false)
        navigate('/loadforms')
      } catch (error) {
        console.error('Error deleting load form:', error)
      }
    }
  }

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false)
  }

  const toggleBillExpansion = (billId: string) => {
    const newExpanded = new Set(expandedBills)
    if (newExpanded.has(billId)) {
      newExpanded.delete(billId)
    } else {
      newExpanded.add(billId)
    }
    setExpandedBills(newExpanded)
  }

  const getStatusVariant = (status: string): 'info' | 'neutral' | 'danger' => {
    switch (status) {
      case 'open':      return 'info'
      case 'closed':    return 'neutral'
      case 'cancelled': return 'danger'
      default:          return 'neutral'
    }
  }

  if (isLoading) {
    return <LoadingSpinner fullScreen message="Loading load form details..." />
  }

  if (error || !loadFormData) {
    return (
      <Box p={3}>
        <Alert severity="error">
          {error ? 'Failed to load load form details' : 'Load form not found'}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/loadforms')}
          sx={{ mt: 2 }}
        >
          Back to Load Forms
        </Button>
      </Box>
    )
  }

  const { loadForm } = loadFormData

  return (
    <Box>
      {/* Header Section */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate('/loadforms')}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4">Load Form Details</Typography>
            <Typography variant="subtitle1" color="text.secondary">
              ID: {loadForm._id}
            </Typography>
          </Box>
        </Box>
        <Box>
          <IconButton onClick={handleMenuOpen}>
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={handlePrint} disabled={printLoadFormMutation.isPending}>
              <PrintIcon sx={{ mr: 1 }} />
              {printLoadFormMutation.isPending ? 'Printing...' : 'Print'}
            </MenuItem>
            <MenuItem onClick={handlePrintWithInvoices} disabled={printLoadFormWithInvoicesMutation.isPending}>
              <PrintIcon sx={{ mr: 1 }} />
              {printLoadFormWithInvoicesMutation.isPending ? 'Printing...' : 'Print with Invoices'}
            </MenuItem>
            <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
              <DeleteIcon sx={{ mr: 1 }} />
              Delete
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {/* Summary Cards - Using API data directly */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Total Amount
                  </Typography>
                  <Typography variant="h5" color="primary.main">
                    Rs {formatCurrency(loadForm.total_amount || 0)}
                  </Typography>
                </Box>
                <MoneyIcon color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Total Quantity
                  </Typography>
                  <Typography variant="h5" color="success.main">
                    {formatQuantity(loadForm.total_quantity)} {uom.code}
                  </Typography>
                </Box>
                <InventoryIcon color="success" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Total Pieces
                  </Typography>
                  <Typography variant="h5" color="info.main">
                    {formatQuantity(loadForm.total_pieces)}
                  </Typography>
                </Box>
                <ReceiptIcon color="info" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Status
                  </Typography>
                  <StatusChip
                    label={(loadForm.status || 'unknown').toUpperCase()}
                    variant={getStatusVariant(loadForm.status)}
                  />
                </Box>
                <TrendingUpIcon color="action" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          variant="fullWidth"
        >
          <Tab label="Overview" />
          <Tab label="Bills" />
          <Tab label="SKUs" />
          <Tab label="Settlement" />
          <Tab label="History" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {activeTab === 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Load Form Overview
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Booker Information
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Name:</strong> {loadForm.booker_id.name}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Email:</strong> {loadForm.booker_id.email}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Area:</strong> {loadForm.area_id?.area_name || 'N/A'}
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Dates
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Created:</strong> {format(new Date(loadForm.createdAt), 'PPpp')}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Updated:</strong> {format(new Date(loadForm.updatedAt), 'PPpp')}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      {activeTab === 1 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Bills ({loadForm.bill_ids.length})
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {/* Using API data directly - SKU names from bill.sku_items */}
          
          {loadForm.bill_ids.length === 0 ? (
            <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
              No bills found
            </Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Customer</TableCell>
                    <TableCell>Area</TableCell>
                    <TableCell>Qty ({uom.code})</TableCell>
                    <TableCell>Discount %</TableCell>
                    <TableCell>Adv Tax %</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loadForm.bill_ids.map((bill: any) => (
                    <React.Fragment key={bill._id}>
                      <TableRow>
                        <TableCell>{bill.customer_name}{bill.customer_id?.customer_code ? ` - ${bill.customer_id.customer_code}` : ''}</TableCell>
                        <TableCell>{bill.area_id?.area_name || 'N/A'}</TableCell>
                        <TableCell>{formatQuantity(bill.summary.quantity_ctn)}</TableCell>
                        <TableCell>{formatQuantity(bill.discount)}%</TableCell>
                        <TableCell>{formatQuantity(bill.adv_tax)}%</TableCell>
                        <TableCell>Rs {formatCurrency(bill.summary.net_payable)}</TableCell>
                        <TableCell>
                          <StatusChip label={bill.status} variant={getStatusVariant(bill.status)} />
                        </TableCell>
                        <TableCell>
                          {format(new Date(bill.createdAt), 'MMM dd, yyyy')}
                        </TableCell>
                        <TableCell>
                          <Box display="flex" gap={1}>
                            <Button
                              size="small"
                              onClick={() => toggleBillExpansion(bill._id)}
                            >
                              {expandedBills.has(bill._id) ? 'Hide' : 'View'} Details
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => window.open(`/bills/${bill._id}`, '_blank')}
                            >
                              Open Bill
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={9} sx={{ py: 0 }}>
                          <Collapse in={expandedBills.has(bill._id)}>
                            <Box sx={{ p: 2, bgcolor: '#faf9f7' }}>
                              <Typography variant="subtitle2" gutterBottom>
                                SKU Details
                              </Typography>
                              <TableContainer>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>SKU Name</TableCell>
                                      <TableCell>Qty ({uom.code})</TableCell>
                                      <TableCell>Qty (PCS)</TableCell>
                                      <TableCell>Price</TableCell>
                                      <TableCell>Amount</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {bill.sku_items.map((sku: any) => {
                                      // Handle case where sku_id is an object with _id and name properties
                                      const getSKUName = (sku: any) => {
                                        if (typeof sku.sku_name === 'string') return sku.sku_name
                                        if (typeof sku.name === 'string') return sku.name
                                        if (typeof sku.sku_id === 'object' && sku.sku_id?.name) return sku.sku_id.name
                                        if (typeof sku.sku_name === 'object' && sku.sku_name?.name) return sku.sku_name.name
                                        if (typeof sku.name === 'object' && sku.name?.name) return sku.name.name
                                        return sku.sku_id || 'Unknown SKU'
                                      }

                                      return (
                                        <TableRow key={sku._id}>
                                          <TableCell>{getSKUName(sku)}</TableCell>
                                          <TableCell>{formatQuantity(sku.quantity_ctn)}</TableCell>
                                          <TableCell>{formatQuantity(sku.quantity_pcs)}</TableCell>
                                          <TableCell>Rs {formatCurrency(sku.price)}</TableCell>
                                          <TableCell>Rs {formatCurrency(sku.amount)}</TableCell>
                                        </TableRow>
                                      )
                                    })}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                              <Box sx={{ mt: 2, p: 2, bgcolor: '#ffffff', border: '1px solid #e8e4dd', borderRadius: 1 }}>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} sm={6}>
                                    <Typography variant="body2">
                                      <strong>Total:</strong> Rs {formatCurrency(bill.summary.total)}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={12} sm={6}>
                                    <Typography variant="body2">
                                      <strong>Advance Tax ({formatQuantity(bill.adv_tax)}%):</strong> Rs {formatCurrency(bill.summary.adv_tax)}
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>Net Payable:</strong> Rs {formatCurrency(bill.summary.net_payable)}
                                    </Typography>
                                  </Grid>
                                </Grid>
                              </Box>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {activeTab === 2 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            SKU Breakdown
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {/* Using API data directly - SKU names, packing, and totals from loadForm.skus */}
          
          {loadForm.skus.length === 0 ? (
            <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
              No SKUs found
            </Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>S.No</TableCell>
                    <TableCell>SKU Name</TableCell>
                    <TableCell>Packing</TableCell>
                    <TableCell>Quantity ({uom.code})</TableCell>
                    <TableCell>Quantity (PCS)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loadForm.skus.map((sku: any, index: number) => {
                    // Handle case where sku_id is an object with _id and name properties
                    const getSKUName = (sku: any) => {
                      if (typeof sku.sku_name === 'string') return sku.sku_name
                      if (typeof sku.name === 'string') return sku.name
                      if (typeof sku.sku_id === 'object' && sku.sku_id?.name) return sku.sku_id.name
                      if (typeof sku.sku_name === 'object' && sku.sku_name?.name) return sku.sku_name.name
                      if (typeof sku.name === 'object' && sku.name?.name) return sku.name.name
                      return sku.sku_id || 'Unknown SKU'
                    }

                    // Calculate packing from quantity_pcs / quantity_ctn if available
                    const getPacking = (sku: any) => {
                      if (sku.packing) return Number(sku.packing)
                      if (sku.pieces_per_ctn) return Number(sku.pieces_per_ctn)
                      if (sku.quantity_ctn && sku.quantity_pcs && sku.quantity_ctn > 0) {
                        return Math.round(Number(sku.quantity_pcs) / Number(sku.quantity_ctn))
                      }
                      return 24 // Default fallback
                    }

                    return (
                      <TableRow key={sku._id || index}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{getSKUName(sku)}</TableCell>
                        <TableCell>{getPacking(sku)}</TableCell>
                        <TableCell>{formatQuantity(sku.quantity_ctn)}</TableCell>
                        <TableCell>{formatQuantity(sku.quantity_pcs)}</TableCell>
                      </TableRow>
                    )
                  })}
                  {/* Total Row - Use API totals if available, otherwise calculate */}
                  <TableRow sx={{ bgcolor: '#f7f5f2', fontWeight: 'bold' }}>
                    <TableCell colSpan={3}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Total
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {formatQuantity(Number(loadForm.total_quantity) || loadForm.skus.reduce((sum: number, sku: any) => sum + (Number(sku?.quantity_ctn) || 0), 0))}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {formatQuantity(Number(loadForm.total_pieces) || loadForm.skus.reduce((sum: number, sku: any) => sum + (Number(sku?.quantity_pcs) || 0), 0))}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {activeTab === 3 && (
        <Paper sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Settlement Details
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate(`/loadforms/${id}/settlement`)}
              startIcon={<MoneyIcon />}
            >
              Manage Settlement
            </Button>
          </Box>
          <Divider sx={{ mb: 2 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Cash Transactions
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Cash Handed Over:</strong> Rs {formatCurrency(loadForm.settlement?.cash_handed_over || 0)}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Cash Collected:</strong> Rs {formatCurrency(loadForm.settlement?.cash_collected || 0)}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Sales on Credit:</strong> Rs {formatCurrency(loadForm.settlement?.sales_on_credit || 0)}
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Expenses & Settlement
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Daily Expense:</strong> Rs {formatCurrency(loadForm.settlement?.daily_expense || 0)}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Unsettled Amount:</strong> Rs {formatCurrency(loadForm.settlement?.unsettled_amount || 0)}
              </Typography>
              {loadForm.settlement?.expense_remarks && (
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Expense Remarks:</strong> {loadForm.settlement.expense_remarks}
                </Typography>
              )}
              {loadForm.settlement?.unsettled_reason && (
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Unsettled Reason:</strong> {loadForm.settlement.unsettled_reason}
                </Typography>
              )}
            </Grid>
          </Grid>

          {/* Credit Sales Details */}
          {loadForm.settlement?.credit_sales && loadForm.settlement.credit_sales.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ color: 'text.primary' }}>
                Credit Sales by Customer
              </Typography>
              <Divider sx={{ mb: 2 }} />
               <TableContainer component={Paper} variant="outlined">
                 <Table size="small">
                   <TableHead>
                     <TableRow>
                       <TableCell>Customer</TableCell>
                       <TableCell>Credit Amount</TableCell>
                       <TableCell>Cheque</TableCell>
                       <TableCell>Remarks</TableCell>
                     </TableRow>
                   </TableHead>
                   <TableBody>
                     {loadForm.settlement.credit_sales.map((credit: any, index: number) => (
                       <TableRow key={`${credit.customer_id}-${index}`}>
                         <TableCell>
                           <Typography variant="body2" fontWeight="medium">
                             {credit.customer_name}
                           </Typography>
                           <Typography variant="caption" color="text.secondary">
                             ID: {credit.customer_id.slice(-8)}
                           </Typography>
                         </TableCell>
                         <TableCell>
                           <Typography variant="body2" fontWeight="medium" color="text.primary">
                             Rs {formatCurrency(credit.amount)}
                           </Typography>
                         </TableCell>
                         <TableCell>
                           {credit.cheque ? (
                             <Box>
                               <Typography variant="body2">Rs {formatCurrency(credit.cheque.amount)} · {credit.cheque.number}</Typography>
                               <Typography variant="caption" color="text.secondary" display="block">
                                 {credit.cheque.bank_name} · {credit.cheque.branch_name}
                               </Typography>
                               <Typography variant="caption" color="text.secondary" display="block">
                                 {credit.cheque.beneficiary_name} · {credit.cheque.cheque_date}
                               </Typography>
                               {credit.cheque.comments && <Typography variant="caption" display="block">{credit.cheque.comments}</Typography>}
                             </Box>
                           ) : '-'}
                         </TableCell>
                         <TableCell>
                           <Typography variant="body2" color="text.secondary">
                             {credit.remarks || '-'}
                           </Typography>
                         </TableCell>
                       </TableRow>
                     ))}
                     {/* Total Row */}
                     <TableRow sx={{ bgcolor: '#f7f5f2', fontWeight: 'bold' }}>
                       <TableCell>
                         <Typography variant="subtitle2" fontWeight="bold">
                           Total Credit Sales
                         </Typography>
                       </TableCell>
                       <TableCell>
                         <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                           Rs {formatCurrency(loadForm.settlement.credit_sales.reduce((sum: number, credit: any) => sum + credit.amount, 0))}
                         </Typography>
                       </TableCell>
                       <TableCell></TableCell>
                       <TableCell></TableCell>
                     </TableRow>
                   </TableBody>
                 </Table>
               </TableContainer>
            </Box>
          )}
        </Paper>
      )}

      {activeTab === 4 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Change History
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          {loadForm.changes.length === 0 ? (
            <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
              No changes recorded
            </Typography>
          ) : (
            <List>
              {loadForm.changes.map((change: any, index: number) => (
                <ListItem key={change._id} divider={index < loadForm.changes.length - 1}>
                  <ListItemText
                    primary={change.description}
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Changed by: {change.changed_by.name} ({change.changed_by.email})
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Date: {format(new Date(change.changed_at), 'PPpp')}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Paper>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Load Form</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this load form? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default LoadFormDetailsPage
